import { useEffect } from 'react'
import { useAppStore } from '@/store'
import { applyResolvedAppearance } from '@/lib/appearancePreferences'

// "Auto" now means time-of-day, not OS preference: light 06:00-17:59,
// dark 18:00-05:59. Assumption, not specified precisely by Dervent --
// a sensible default (daylight-hours convention), easy to adjust if a
// different cutoff is wanted.
const LIGHT_START_HOUR = 6
const DARK_START_HOUR = 18

function timeOfDayTheme(): 'light' | 'dark' {
  const hour = new Date().getHours()
  return hour >= LIGHT_START_HOUR && hour < DARK_START_HOUR ? 'light' : 'dark'
}

export function useTheme() {
  const { theme, setResolvedTheme } = useAppStore()

  useEffect(() => {
    const root = document.documentElement

    function apply() {
      const resolved = theme === 'auto' ? timeOfDayTheme() : theme
      root.classList.toggle('dark', resolved === 'dark')
      applyResolvedAppearance(resolved, root)
      setResolvedTheme(resolved)
    }

    apply()

    // Time-of-day can only be re-evaluated on a timer, not an event --
    // re-check every minute so the transition at 06:00/18:00 happens
    // promptly without needing a manual refresh, while staying cheap
    // enough to run indefinitely.
    const interval = theme === 'auto' ? window.setInterval(apply, 60_000) : null
    return () => { if (interval) window.clearInterval(interval) }
  }, [theme, setResolvedTheme])
}
